import React from "react";
import styled from "styled-components";
import { bodyType, colorTransition, focusRing, iconSlot } from "./base/mixins";

export interface TabItem {
	key: string;
	label: React.ReactNode;
	icon?: React.ReactNode;
	disabled?: boolean;
}

export interface TabGroupProps {
	items: TabItem[];
	activeKey: string;
	onChange: (key: string) => void;
	/** Accessible name for the tablist, e.g. "Panel sections". */
	label: string;
	className?: string;
}

/**
 * Panel navigation — it changes *what the panel shows*.
 *
 * §8 is emphatic that this is **structurally different from a SegmentedControl**
 * and that the two are never substituted. They are separate components with no
 * shared props precisely so that cannot happen by accident:
 *
 *   TabGroup          items are joined, 48px, borders overlap, active = purple border
 *   SegmentedControl  items are separated by 6px, 34px, active = purple fill
 *
 * If you are picking a value for a setting, you want SegmentedControl.
 */
const TabGroup = ({ items, activeKey, onChange, label, className }: TabGroupProps) => (
	<List role="tablist" aria-label={label} className={className}>
		{items.map((item) => {
			const active = item.key === activeKey;
			return (
				<Tab
					key={item.key}
					type="button"
					role="tab"
					aria-selected={active}
					disabled={item.disabled}
					$active={active}
					onClick={() => onChange(item.key)}
				>
					{item.icon ? <span className="hds-tab__icon">{item.icon}</span> : null}
					{item.label}
				</Tab>
			);
		})}
	</List>
);

const List = styled.div`
	display: flex;
	width: 100%;

	/* Radius on the group's outer corners only — the items read as one control. */
	> button:first-child {
		border-top-left-radius: var(--radius-md);
		border-bottom-left-radius: var(--radius-md);
	}
	> button:last-child {
		border-top-right-radius: var(--radius-md);
		border-bottom-right-radius: var(--radius-md);
	}
`;

const Tab = styled.button<{ $active: boolean }>`
	flex: 1;
	min-width: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: var(--space-2);
	height: var(--size-control);
	padding: 0 var(--space-3);
	border: var(--border-default);
	border-radius: 0;
	/* Joined: the shared edge is one line, not two. */
	margin-left: -1px;
	&:first-child {
		margin-left: 0;
	}
	background: var(--color-surface);
	cursor: pointer;
	box-shadow: none;
	${bodyType}
	${colorTransition}
	${focusRing}

	/* Icons stay #1F1F1F whether the tab is active or not (§6). */
	.hds-tab__icon {
		${iconSlot}
		color: var(--color-text);
	}

	&:hover:not(:disabled) {
		background: var(--color-hover-ground);
		border-color: var(--color-border-strong);
	}

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}

	${({ $active }) =>
		$active &&
		`
		background: var(--color-selected-ground);
		border-color: var(--color-secondary);
		/* So the purple border sits above its neighbours' grey ones. */
		position: relative;
		z-index: 1;
	`}
`;

export default TabGroup;
